import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// One dialog finding inside a cluster container. Three modes, one component:
//   selectable — a proposed cluster's member, included in the promote unless unticked
//   static     — an anchored theme's existing evidence, nothing to decide
//   pending    — an assignment proposed onto an anchored theme, accept or reject
//
// Tiles are visually flatter than an assumption sticky because the container is
// the unit of consequence here: promote, reject and merge all act on the
// container, while a tile only ever includes or excludes itself. It is still
// interactive in selectable mode, so it carries a pointer and a hover state.
export function MemberTile({
  title,
  mode,
  checked = true,
  onToggle,
  onAccept,
  onReject,
  disabled,
}: {
  title: string;
  mode: 'selectable' | 'static' | 'pending';
  checked?: boolean;
  onToggle?: () => void;
  onAccept?: () => void;
  onReject?: () => void;
  disabled?: boolean;
}) {
  // line-clamp-3 rather than trusting the data: the ~60-character title the
  // extraction prompt asks for is only a hint, and a respondent can edit a
  // title up to 2000 characters. The clamp is visual only, so the full string
  // stays in the DOM and in the checkbox's accessible name.
  const text = <span className="line-clamp-3">{title}</span>;
  const base = 'w-[7.6rem] rounded-md border bg-card p-1.5 text-[10px] leading-snug text-muted-foreground';

  if (mode === 'selectable') {
    return (
      <label
        className={cn(
          base,
          'flex cursor-pointer gap-1.5 transition-opacity hover:border-primary/40',
          !checked && 'opacity-50',
          disabled && 'cursor-default opacity-60',
        )}
      >
        {/* No aria-label: the visible title is the label's own text, so the
            checkbox takes its accessible name by implicit association. */}
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          disabled={disabled}
          className="mt-0.5 h-3 w-3 shrink-0 accent-primary"
        />
        {text}
      </label>
    );
  }

  if (mode === 'pending') {
    return (
      <div className={cn(base, 'border-dashed')}>
        {text}
        <div className="mt-1.5 flex justify-end gap-1">
          <button
            type="button"
            onClick={onAccept}
            disabled={disabled}
            aria-label={`Add to this theme: ${title}`}
            className="rounded p-0.5 text-primary hover:bg-primary/10 disabled:opacity-50"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={onReject}
            disabled={disabled}
            aria-label={`Leave out: ${title}`}
            className="rounded p-0.5 hover:text-destructive disabled:opacity-50"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  return <div className={base}>{text}</div>;
}
